import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";

export interface Capability {
  id: string;
  tenant_id: string;
  role: string;
  key: string;
  value: boolean;
  created_at: string;
  updated_at: string;
}

export const useCapabilities = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();

  return useQuery({
    queryKey: ["capabilities", currentTenant?.id, user?.id],
    queryFn: async () => {
      if (!currentTenant?.id || !user?.id) {
        console.warn("useCapabilities: Missing tenant or user context");
        return [];
      }

      const { data, error } = await supabase
        .from("role_capability")
        .select("*")
        .eq("tenant_id", currentTenant.id);

      if (error) {
        console.error("Failed to load capabilities:", error);
        throw error;
      }

      return data as Capability[];
    },
    enabled: !!currentTenant?.id && !!user?.id,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (prev) => prev,
  });
};


export const useUserCapabilities = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-capabilities", currentTenant?.id, user?.id],
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      if (!currentTenant?.id || !user?.id) return [];

      // Get user's role in current tenant
      const { data: membership } = await supabase
        .from("memberships")
        .select("role")
        .eq("tenant_id", currentTenant.id)
        .eq("user_id", user.id)
        .single();

      if (!membership) return [];

      // Get role-based capabilities
      const { data: roleCapabilities = [] } = await supabase
        .from("role_capability")
        .select("*")
        .eq("tenant_id", currentTenant.id)
        .eq("role", membership.role);

      // Get user-specific capability overrides
      const { data: userOverrides = [] } = await supabase
        .from("user_capability")
        .select("*")
        .eq("tenant_id", currentTenant.id)
        .eq("user_id", user.id);

      // Merge capabilities with user overrides taking precedence
      const capabilityMap = new Map();

      roleCapabilities.forEach(cap => {
        capabilityMap.set(cap.key, cap);
      });

      userOverrides.forEach(override => {
        capabilityMap.set(override.key, {
          ...override,
          role: membership.role,
        });
      });

      return Array.from(capabilityMap.values()).filter(cap => cap.value) as Capability[];
    },
    enabled: !!currentTenant?.id && !!user?.id,
  });
};


export const useUserCapabilityOverrides = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-capability-overrides", currentTenant?.id, user?.id],
    queryFn: async () => {
      if (!currentTenant?.id || !user?.id) return [];

      const { data, error } = await supabase
        .from("user_capability")
        .select("*")
        .eq("tenant_id", currentTenant.id)
        .eq("user_id", user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant?.id && !!user?.id,
  });
};

// Treat "query is disabled because tenant/user not ready yet" as loading.
// Otherwise callers get `isLoading = false` with empty data and mistakenly
// render "Access Denied" during a brief post-navigation race.
const useReadyUserCapabilities = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const notReady = !currentTenant?.id || !user?.id;
  const { data = [], isLoading } = useUserCapabilities();
  return { data, isLoading: notReady || isLoading };
};

export const useCan = (capability: string) => {
  const { data: capabilities, isLoading } = useReadyUserCapabilities();

  if (isLoading) return false;
  return capabilities.some(cap => cap.key === capability && cap.value);
};

export const useCanWithLoading = (capability: string) => {
  const { data: capabilities, isLoading } = useReadyUserCapabilities();

  const hasCapability = isLoading
    ? false
    : capabilities.some(cap => cap.key === capability && cap.value);

  return { can: hasCapability, isLoading };
};

export const useCanWithOverride = (capability: string, userId?: string) => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  return useQuery({
    queryKey: ["can-with-override", capability, currentTenant?.id, targetUserId],
    queryFn: async () => {
      if (!currentTenant?.id || !targetUserId) return false;

      const { data, error } = await supabase.rpc("can_with_overrides", {
        _capability_key: capability,
        _user_id: targetUserId,
        _tenant_id: currentTenant.id,
      });

      if (error) throw error;
      return data as boolean;
    },
    enabled: !!currentTenant?.id && !!targetUserId,
  });
};

export const useCanMultiple = (capabilities: string[]) => {
  const { data: userCapabilities, isLoading } = useReadyUserCapabilities();

  const results = capabilities.reduce((acc, capability) => {
    acc[capability] = isLoading ? false : userCapabilities.some(cap => cap.key === capability && cap.value);
    return acc;
  }, {} as Record<string, boolean>);

  return { capabilities: results, isLoading };
};
